import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Squares2X2Icon, CubeIcon } from '@heroicons/react/24/outline';
import { Category } from '../types';
import { ApiService } from '../services/api';

/**
 * 카테고리 페이지 — 2026-08-30 신설.
 *
 * 🔴 헤더의 「카테고리」 링크가 이 주소를 가리키는데 라우트도 백엔드 API 도 없어
 *    누르면 404 였다. 데이터(카테고리 9건)는 처음부터 있었다 — 만들다 만 자리였다.
 * 🔑 여기서 상품을 다시 그리지 않는다. 고른 카테고리를 그대로 상품 목록의 필터로 넘긴다 —
 *    목록·정렬·페이지 로직이 ProductsPage 한 곳에만 있어야 나중에 갈라지지 않는다.
 */
const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await ApiService.getCategories();
        if (res.success && res.data) setCategories(res.data);
        else setError('카테고리를 불러오지 못했습니다.');
      } catch {
        // 🔑 실패를 조용히 넘기지 않는다. 빈 화면은 「없다」와 「못 불러왔다」를 구별해 주지 않는다.
        setError('카테고리를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Squares2X2Icon className="w-8 h-8 text-blue-600" />
          카테고리
        </h1>
        <p className="mt-2 text-gray-600">찾으시는 분류를 고르면 해당 상품만 모아 보여 드립니다.</p>
      </div>

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
      )}

      {!isLoading && !error && categories.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
          등록된 카테고리가 아직 없습니다.
        </div>
      )}

      {!isLoading && !error && categories.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <div key={c.id} className="rounded-xl border border-gray-200 bg-white p-5 hover:shadow-md transition-shadow">
              <Link to={`/products?category=${c.id}`} className="flex items-center justify-between group">
                <span className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">{c.name}</span>
                <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                  <CubeIcon className="w-4 h-4" />
                  {c.productCount}
                </span>
              </Link>
              {c.children && c.children.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.children.map((sub) => (
                    <Link
                      key={sub.id}
                      to={`/products?category=${sub.id}`}
                      className="px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                    >
                      {sub.name} ({sub.productCount})
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoriesPage;
